import React, { Component } from "react";
import { ProductConsumer } from "../../Context/AddProWakel";
import {
    SUB_TABLE_KEYS,
    fieldLabel,
    modelLabel,
    actionLabel,
    formatDate,
    formatFieldValue,
    diffFields,
    snapshotFields,
    subTableKeys,
    valuesEqual,
    recordIdentity,
    EMPTY,
} from "./auditFields";
import "../shared/rtl-panel.css";

class ModelAuditLog extends Component {
    // Keyed by log id so re-opening a different entry starts collapsed again.
    state = { showAllFor: null };

    toggleShowAll = (logId) =>
        this.setState((prev) => ({
            showAllFor: prev.showAllFor === logId ? null : logId,
        }));

    handlePrint = (log, rows) => {
        const printWindow = window.open("", "_blank", "width=900,height=700");
        if (!printWindow) return;

        const isDelete = log.action === "DELETE";

        const bodyRows = isDelete
            ? rows
                .map(
                    (row) => `<tr>
              <th>${fieldLabel(row.key)}</th>
              <td>${row.value}</td>
            </tr>`
                )
                .join("")
            : rows
                .map(
                    (row) => `<tr>
              <th>${fieldLabel(row.key)}</th>
              <td class="${row.changed ? "old" : ""}">${row.before}</td>
              <td class="${row.changed ? "new" : ""}">${row.after}</td>
            </tr>`
                )
                .join("");

        const head = isDelete
            ? "<tr><th>خانە</th><th>بەها</th></tr>"
            : "<tr><th>خانە</th><th>کۆن</th><th>نوێ</th></tr>";

        printWindow.document.write(`<!DOCTYPE html>
<html dir="rtl" lang="ku">
<head>
  <meta charset="UTF-8">
  <title>بەشی وردبینی — ${modelLabel(log.modelName)}</title>
  <style>
    @page { size: A4; margin: 15mm; }
    body { font-family: 'Segoe UI', Tahoma, sans-serif; direction: rtl; padding: 16px; }
    h2 { text-align: center; margin: 0 0 14px; }
    .info { display: flex; flex-wrap: wrap; gap: 6px 26px; margin-bottom: 16px; font-size: 14px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 7px 12px; border: 1px solid #bbb; text-align: right; font-size: 13px; }
    thead th { background: #f1f4f6; color: #1e2a30; }
    tbody th { background: #f2f2f2; width: 200px; }
    td.old { background: #fdecea; color: #b3261e; text-decoration: line-through; }
    td.new { background: #e6f4ea; color: #1e7e34; }
  </style>
</head>
<body>
  <h2>بەشی وردبینی</h2>
  <div class="info">
    <span><b>بەش:</b> ${modelLabel(log.modelName)}</span>
    <span><b>کردار:</b> ${actionLabel(log.action)}</span>
    <span><b>لەلایەن:</b> ${log.performedBy ? log.performedBy.name : "نەزانراو"}</span>
    <span><b>بەروار:</b> ${formatDate(log.createdAt)}</span>
  </div>
  <table>
    <thead>${head}</thead>
    <tbody>${bodyRows}</tbody>
  </table>
  <script>window.onload = function () { window.print(); }</script>
</body>
</html>`);
        printWindow.document.close();
    };

    // `isSnapshot` (a DELETE) has no "after" rows to compare against, so the
    // line items are printed as plain values rather than a diff into dashes.
    renderSubTable = (key, oldArr, newArr, isSnapshot) => {
        const oldItems = Array.isArray(oldArr) ? oldArr : [];
        const newItems = Array.isArray(newArr) ? newArr : [];
        const rowCount = Math.max(oldItems.length, newItems.length);
        if (rowCount === 0) return null;

        const keys = subTableKeys(oldItems, newItems);
        if (keys.length === 0) return null;

        return (
            <div className="rs-section" key={key}>
                <h6 className="rs-section__title">
                    <i className="fas fa-table"></i>
                    {fieldLabel(key)}
                    <span className="rs-section__aside rs-pager__count rs-num">
                        {rowCount} ڕیز
                    </span>
                </h6>
                <div className="rs-card">
                    <table className="rs-table rs-diff">
                        <thead>
                            <tr>
                                <th className="is-center" style={{ width: "48px" }}>
                                    #
                                </th>
                                {keys.map((subKey) => (
                                    <th key={subKey}>{fieldLabel(subKey)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: rowCount }, (_, i) => {
                                const before = oldItems[i] || {};
                                const after = newItems[i] || {};
                                return (
                                    <tr key={i}>
                                        <td className="is-center rs-idx">{i + 1}</td>
                                        {keys.map((subKey) => {
                                            const oldValue = formatFieldValue(subKey, before[subKey]);
                                            const newValue = formatFieldValue(subKey, after[subKey]);
                                            if (isSnapshot || valuesEqual(before[subKey], after[subKey])) {
                                                return <td key={subKey}>{oldValue}</td>;
                                            }
                                            return (
                                                <td key={subKey}>
                                                    <span className="rs-was">{oldValue}</span>
                                                    <i className="fas fa-arrow-left rs-arrow"></i>
                                                    <span className="rs-now">{newValue}</span>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    renderSnapshot = (rows) => (
        <div className="rs-card">
            <table className="rs-table rs-diff">
                <thead>
                    <tr>
                        <th style={{ width: "220px" }}>خانە</th>
                        <th>بەها</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key}>
                            <td className="rs-diff__key">{fieldLabel(row.key)}</td>
                            <td>{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    renderDiff = (rows) => (
        <div className="rs-card">
            <table className="rs-table rs-diff">
                <thead>
                    <tr>
                        <th style={{ width: "220px" }}>خانە</th>
                        <th>کۆن</th>
                        <th>نوێ</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key}>
                            <td className="rs-diff__key">{fieldLabel(row.key)}</td>
                            <td className={row.changed ? "rs-diff__old" : "is-same"}>
                                {row.before}
                            </td>
                            <td className={row.changed ? "rs-diff__new" : "is-same"}>
                                {row.after}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    render() {
        return (
            <ProductConsumer>
                {(value) => {
                    const { AL_modalOpen, AL_selectedLog, AL_CloseModal } = value;
                    if (!AL_modalOpen || !AL_selectedLog) return null;

                    const log = AL_selectedLog;
                    const isDelete = log.action === "DELETE";
                    const showAll = this.state.showAllFor === log._id;
                    // A product's code + name, or on an invoice the customer's
                    // number + name — the same identity the list row shows.
                    const identity = recordIdentity(log);

                    // DELETE has no "after" state — show the removed record instead of
                    // a diff where every new value is a dash.
                    const rows = isDelete
                        ? snapshotFields(log.oldData)
                        : diffFields(log.oldData, log.newData, !showAll);

                    const changedCount = diffFields(log.oldData, log.newData).length;

                    return (
                        <div className="rs-modal" dir="rtl" onClick={AL_CloseModal}>
                            <div
                                className="rs-modal__panel"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="rs-modal__head">
                                    <h5 className="rs-modal__title">
                                        <i className="fas fa-info-circle"></i>
                                        وردەکاری تۆمار
                                    </h5>
                                    <span
                                        className={`rs-badge ${isDelete ? "rs-badge--red" : "rs-badge--amber"
                                            }`}
                                    >
                                        {actionLabel(log.action)} — {modelLabel(log.modelName)}
                                    </span>

                                    <div className="rs-modal__actions">
                                        <button
                                            className="rs-btn rs-btn--ghost rs-btn--sm"
                                            onClick={() => this.handlePrint(log, rows)}
                                        >
                                            <i className="fas fa-print"></i>
                                            چاپکردن
                                        </button>
                                        <button
                                            className="rs-btn rs-btn--danger rs-btn--sm"
                                            onClick={AL_CloseModal}
                                        >
                                            <i className="fas fa-times"></i>
                                            داخستن
                                        </button>
                                    </div>
                                </div>

                                <div className="rs-modal__body">
                                    {identity && (identity.code || identity.name) ? (
                                        <div className="rs-product-banner">
                                            <i
                                                className={`fas ${identity.kind === "customer"
                                                    ? "fa-user"
                                                    : "fa-box"
                                                    }`}
                                            ></i>
                                            {identity.code ? (
                                                <span className="rs-product-banner__code rs-num">
                                                    {identity.code}
                                                </span>
                                            ) : null}
                                            {identity.name ? (
                                                <span className="rs-product-banner__name">
                                                    {identity.name}
                                                </span>
                                            ) : null}
                                        </div>
                                    ) : null}

                                    <div className="rs-meta">
                                        <div>
                                            <span className="rs-meta__k">لەلایەن</span>
                                            <span className="rs-meta__v">
                                                {log.performedBy ? log.performedBy.name : "نەزانراو"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="rs-meta__k">بەروار و کات</span>
                                            <span className="rs-meta__v rs-num">
                                                {formatDate(log.createdAt)}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="rs-meta__k">ئایپی</span>
                                            <span className="rs-meta__v rs-num">
                                                {log.ipAddress || EMPTY}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="rs-meta__k">ژمارەی بەڵگە</span>
                                            <span className="rs-meta__v rs-num" style={{ fontSize: "0.9rem" }}>
                                                {log.documentId || EMPTY}
                                            </span>
                                        </div>
                                    </div>

                                    <h6 className="rs-section__title">
                                        <i className={`fas ${isDelete ? "fa-trash" : "fa-exchange-alt"}`}></i>
                                        {isDelete ? "زانیاری سڕاوە" : "گۆڕانکارییەکان"}
                                        {!isDelete && (
                                            <span className="rs-section__aside">
                                                <button
                                                    className="rs-btn rs-btn--ghost rs-btn--sm"
                                                    onClick={() => this.toggleShowAll(log._id)}
                                                >
                                                    <i className={`fas ${showAll ? "fa-compress-alt" : "fa-expand-alt"}`}></i>
                                                    {showAll
                                                        ? `تەنها گۆڕاوەکان (${changedCount})`
                                                        : "پیشاندانی هەموو خانەکان"}
                                                </button>
                                            </span>
                                        )}
                                    </h6>

                                    {rows.length === 0 ? (
                                        <div className="rs-empty">
                                            <i className="fas fa-equals"></i>
                                            <p>هیچ گۆڕانکارییەک لە خانەکاندا نەکراوە</p>
                                        </div>
                                    ) : isDelete ? (
                                        this.renderSnapshot(rows)
                                    ) : (
                                        this.renderDiff(rows)
                                    )}

                                    {SUB_TABLE_KEYS.map((key) => {
                                        const before = (log.oldData || {})[key];
                                        const after = (log.newData || {})[key];
                                        if (!before && !after) return null;
                                        return this.renderSubTable(key, before, after, isDelete);
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                }}
            </ProductConsumer>
        );
    }
}

export default ModelAuditLog;
